import { buildAppsheetVmtRecord, getTodayVmtDate } from '../detailPanel/appsheet/appsheetVmtMapping.js';
import { formatEvidenceDate } from './DrawerEvidenceHelpers.jsx';

export const DPE_ACTION_MENU = {
  ADD_FROM_APPSHEET: 'dpe_add_from_appsheet',
  CANCEL_FROM_DPE: 'dpe_cancel_from_dpe',
};

export const DPE_ACTION_FIELDS = [
  'referenceId',
  'dealerPrice',
  'filler',
  'gmBrandCd',
  'vendorCd',
  'effectiveDate',
  'selectionDate',
  'billingDate',
  'lastUpdatedDate',
  'productCd',
  'productDescription',
  'designation',
  'status',
];

const PRODUCT_CODE_TO_NAME = [
  [/^DI_B_/i, 'Base'],
  [/^DI_P1_/i, 'Plus'],
  [/^DI_P_/i, 'Premier'],
];
const PACKAGE_CODE_PATTERNS = [/^DI_B(?:_|$)/i, /^DI_P1(?:_|$)/i, /^DI_P(?:_|$)/i];

const pick = (row = {}, keys = []) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return '';
};
const normalizeStatus = (status) => {
  const raw = String(status || '').trim().toLowerCase();
  if (raw === 'pending live') return 'Pending Live';
  if (raw === 'pending cancel') return 'Pending Cancel';
  if (raw === 'cancelled' || raw === 'canceled') return 'Cancelled';
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Live';
};
export const productDescriptionForCode = (code = '', currentDescription = '') => {
  const inferred = PRODUCT_CODE_TO_NAME.find(([pattern]) => pattern.test(code))?.[1] || '';
  const current = String(currentDescription || '').trim();
  if (!inferred) return current || code;
  if (!current || /^website package$/i.test(current)) return inferred;
  return current;
};
export const designationForCode = (code = '') => (
  PACKAGE_CODE_PATTERNS.some((pattern) => pattern.test(code)) ? 'PACKAGE' : 'A LA CARTE'
);
const vendorCodeForProgram = (...values) => {
  const source = values.map((value) => String(value || '').trim().toLowerCase()).find(Boolean) || '';
  if (source.includes('trade') || source.includes('accutrade')) return '84';
  if (source.includes('website') || source.includes('chat')) return '5D';
  return '';
};
const normalizeProgramKey = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();
const CATEGORY_CODE_ALIASES = [
  { code: 'TRTL', aliases: ['trade', 'accutrade', 'gm accutrade'] },
  { code: 'CHAT', aliases: ['chat', 'gm chat'] },
  { code: 'DGA', aliases: ['digital ad sales', 'digital advertising sales', 'digad sales', 'dig ad sales', 'gm digad sales'] },
  { code: 'SDGA', aliases: ['digital ad service', 'digital advertising service', 'digad service', 'dig ad service', 'gm digad service'] },
  { code: 'SEO', aliases: ['seo', 'gm seo'] },
  { code: 'DLRWST', aliases: ['website', 'web', 'dealer website', 'gm website'] },
];
const categoryCodeForProgram = (...values) => {
  const keys = values.map(normalizeProgramKey).filter(Boolean);
  for (const { code, aliases } of CATEGORY_CODE_ALIASES) {
    if (keys.some((key) => aliases.some((alias) => key === alias || key.includes(alias)))) return code;
  }
  return 'DLRWST';
};
const normalizeBrandCode = (value = '') => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/^AT_AT[CS]_+/, '')
  .replace(/^AT_ACS_+/, '')
  .replace(/^ACS_+/, '')
  .replace(/^_+/, '')
  .replace(/_SEC$/, '')
  .replace(/^(?:B|P|P1)_+([A-Z]{1,3})$/, '$1')
  .replace(/^BCG$/, 'CBG');
const sourceRow = (context = {}) => context.row || context.sourceEvidence || {};
const evidenceOrderItemId = (context = {}, evidence = {}) => (
  context.source === 'cpq'
    ? context.matchedAppSheetOrderItemId
      || evidence.matchedAppSheetOrderItemId
      || evidence.accountMatch?.orderItemId
      || context.orderItemId
      || evidence.orderItemId
    : context.orderItemId || evidence.orderItemId
);
const dateFromEvidence = (evidence = {}, keys = []) => formatEvidenceDate(pick(evidence, keys));

const EVIDENCE_DATE_KEYS = {
  effectiveDate: ['effectiveDate', 'EffectiveDate', 'Effective Date', 'activationDate', 'goLiveDate', 'billingStartDate', 'Billing Start Date', 'orderStartDate', 'enrollmentDate'],
  selectionDate: ['selectionDate', 'SelectionDate', 'Product Selection Date', 'productSelectionDate', 'productSelectionDateDisplay', 'selectedAt', 'selectionDt', 'orderStartDate', 'effectiveDate'],
  billingDate: ['billingDate', 'BillingDate', 'Billing Date', 'billingStartDate', 'Billing Start Date', 'billingStart', 'billingStartDt', 'orderStartDate', 'effectiveDate'],
  lastUpdatedDate: ['lastUpdatedDate', 'LastUpdatedDate', 'Last Updated Date', 'lastModified', 'LastModifiedDate', 'updatedAt'],
};

export const buildDpeActionDraftFromContext = ({
  actionType = 'add',
  context = {},
  targetContext = {},
  row = {},
  serviceMonth,
  currentUserName,
} = {}) => {
  const evidence = sourceRow(context);
  const productCode = context.productCode || pick(evidence, ['productCode', 'productCd', 'Product Code']);
  const productName = context.productName || pick(evidence, ['productName', 'productDescription', 'serviceItemName', 'Product Name']);
  const programName = context.programName || pick(evidence, ['programName', 'Program', 'program']) || row.program || row.programName;
  const evidenceBrand = normalizeBrandCode(pick(evidence, ['gmBrandCd', 'brandCode']));
  const gmBrandCd = evidenceBrand;
  const vendorCd = vendorCodeForProgram(programName, context.source, targetContext.source)
    || (context.source === 'appsheet' ? '5D' : pick(evidence, ['vendorCd', 'vendorCode', 'venderCd', 'VenderCd']))
    || '5D';
  const categoryCd = categoryCodeForProgram(programName, context.source, targetContext.source);
  const effectiveDateFromEvidence = dateFromEvidence(evidence, EVIDENCE_DATE_KEYS.effectiveDate);
  const selectionDateFromEvidence = dateFromEvidence(evidence, EVIDENCE_DATE_KEYS.selectionDate);
  const billingDateFromEvidence = dateFromEvidence(evidence, EVIDENCE_DATE_KEYS.billingDate);
  const lastUpdatedDateFromEvidence = dateFromEvidence(evidence, EVIDENCE_DATE_KEYS.lastUpdatedDate);
  const fillerFromEvidence = pick(evidence, ['filler', 'Filler', 'gmCost', 'gmFee']);
  const base = context.source === 'appsheet'
    ? buildAppsheetVmtRecord(evidence, {
      stampLastUpdatedDate: true,
      defaults: {
        bac: row.bac,
        categoryCd,
        vendorCd,
        gmBrandCd,
        occurrence: 'Monthly',
        itemQuantity: '1',
        designation: designationForCode(productCode),
        lastUpdatedBy: currentUserName,
      },
      overrides: {
        categoryCd,
        vendorCd,
        productCd: productCode,
        productDescription: productName,
        gmBrandCd,
        designation: designationForCode(productCode),
        dealerPrice: context.price ?? pick(evidence, ['mrr', 'dealerPrice']),
        filler: fillerFromEvidence,
        effectiveDate: effectiveDateFromEvidence,
        selectionDate: selectionDateFromEvidence,
        billingDate: billingDateFromEvidence,
        referenceId: context.referenceId || context.orderItemId || pick(evidence, ['referenceId', 'id']),
        serviceMonth,
      },
    })
    : {
      bac: row.bac,
      countryCd: 'US',
      categoryCd,
      vendorCd,
      gmBrandCd,
      productCd: productCode,
      productDescription: productName || productCode,
      designation: pick(evidence, ['designation']) || designationForCode(productCode),
      occurrence: 'Monthly',
      itemQuantity: '1',
      dealerPrice: context.price ?? pick(evidence, ['dealerPrice', 'mrr']),
      filler: fillerFromEvidence,
      status: normalizeStatus(context.status),
      selectionDate: selectionDateFromEvidence,
      effectiveDate: effectiveDateFromEvidence,
      billingDate: billingDateFromEvidence,
      lastUpdatedDate: getTodayVmtDate(),
      lastUpdatedBy: currentUserName,
      referenceId: context.referenceId || evidence.referenceId || evidenceOrderItemId(context, evidence),
      serviceMonth,
    };
  const status = actionType === 'remove' ? 'Cancelled' : normalizeStatus(base.status || context.status);
  const effectiveDate = formatEvidenceDate(base.effectiveDate) || effectiveDateFromEvidence;
  const selectionDate = formatEvidenceDate(base.selectionDate) || selectionDateFromEvidence || effectiveDate;
  const billingDate = formatEvidenceDate(base.billingDate) || billingDateFromEvidence || selectionDate;
  const lastUpdatedDate = formatEvidenceDate(base.lastUpdatedDate) || lastUpdatedDateFromEvidence || getTodayVmtDate();
  const vmtPayload = {
    ...base,
    bac: String(row.bac || base.bac || ''),
    vendorCd: vendorCd || base.vendorCd,
    status,
    productCd: base.productCd || productCode,
    productDescription: productDescriptionForCode(base.productCd || productCode, base.productDescription || productName),
    designation: designationForCode(base.productCd || productCode),
    effectiveDate,
    selectionDate,
    billingDate,
    lastUpdatedDate,
    lastUpdatedBy: base.lastUpdatedBy || currentUserName,
  };
  return {
    runId: row.runId,
    varianceId: row.id,
    bac: row.bac,
    dealerName: row.dealerName,
    actionType,
    queueStatus: 'draft',
    vmtPayload,
    sourceEvidence: { source: context.source, ...evidence },
    targetEvidence: targetContext || {},
    metadata: { createdFrom: 'billing_dpe_action_modal' },
  };
};

export const dpeActionLabel = (type) => {
  if (type === 'remove') return 'Remove product from DPE';
  if (type === 'change_price') return 'Change DPE price';
  return 'Add product to DPE';
};

export const statusPatch = (status, today = getTodayVmtDate()) => ({
  status,
  lastUpdatedDate: today,
});
